import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../services/api.js'

export default function ActivitiesPage() {
  const [tab, setTab] = useState('atividades')
  const [activities, setActivities] = useState([])
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // ── Atividades state ───────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState(null)
  const [editLabel, setEditLabel] = useState('')
  const [saving, setSaving] = useState(false)
  const [showNewForm, setShowNewForm] = useState(false)
  const [newLabel, setNewLabel] = useState('')
  const [creating, setCreating] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  // ── Cronograma state ───────────────────────────────────────────────────────
  const [showRuleForm, setShowRuleForm] = useState(false)
  const [editingRule, setEditingRule] = useState(null)  // null | rule obj
  const [ruleForm, setRuleForm] = useState({ trigger_key: '', target_key: '', offset_days: 7 })
  const [savingRule, setSavingRule] = useState(false)
  const [deletingRuleId, setDeletingRuleId] = useState(null)

  async function fetchAll() {
    setLoading(true)
    try {
      const [acts, rls] = await Promise.all([api.getActivities(), api.getCalendarRules()])
      setActivities(acts)
      setRules(rls)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [])

  // ── Atividades handlers ────────────────────────────────────────────────────
  async function handleToggle(act) {
    try {
      const updated = await api.updateActivity(act.id, { enabled: !act.enabled })
      setActivities(prev => prev.map(a => a.id === updated.id ? updated : a))
    } catch (e) { setError(e.message) }
  }

  function startEdit(act) { setEditingId(act.id); setEditLabel(act.label) }

  async function saveLabel(act) {
    if (!editLabel.trim()) return
    setSaving(true)
    try {
      const updated = await api.updateActivity(act.id, { label: editLabel.trim() })
      setActivities(prev => prev.map(a => a.id === updated.id ? updated : a))
      setEditingId(null)
    } catch (e) { setError(e.message) }
    finally { setSaving(false) }
  }

  async function handleReorder(idx, direction) {
    const sorted = [...activities].sort((a, b) => a.display_order - b.display_order)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= sorted.length) return
    const ids = sorted.map(a => a.id)
    ;[ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]]
    const items = ids.map((id, i) => ({ id, display_order: i + 1 }))
    try {
      const updated = await api.reorderActivities(items)
      setActivities(updated)
    } catch (e) { setError(e.message) }
  }

  async function handleCreate() {
    if (!newLabel.trim()) return
    setCreating(true)
    try {
      const created = await api.createActivity({ label: newLabel.trim() })
      setActivities(prev => [...prev, created])
      setNewLabel('')
      setShowNewForm(false)
    } catch (e) { setError(e.message) }
    finally { setCreating(false) }
  }

  async function handleDelete(act) {
    try {
      await api.deleteActivity(act.id)
      setActivities(prev => prev.filter(a => a.id !== act.id))
      setDeletingId(null)
    } catch (e) { setError(e.message); setDeletingId(null) }
  }

  // ── Calendar rule handlers ─────────────────────────────────────────────────
  function openNewRule() {
    setEditingRule(null)
    setRuleForm({ trigger_key: '', target_key: '', offset_days: 7 })
    setShowRuleForm(true)
  }

  function openEditRule(rule) {
    setEditingRule(rule)
    setRuleForm({ trigger_key: rule.trigger_key, target_key: rule.target_key, offset_days: rule.offset_days })
    setShowRuleForm(true)
  }

  async function handleSaveRule() {
    if (!ruleForm.trigger_key || !ruleForm.target_key || !ruleForm.offset_days) return
    setSavingRule(true)
    try {
      if (editingRule) {
        const updated = await api.updateCalendarRule(editingRule.id, ruleForm)
        setRules(prev => prev.map(r => r.id === updated.id ? updated : r))
      } else {
        const created = await api.createCalendarRule(ruleForm)
        setRules(prev => [...prev, created])
      }
      setShowRuleForm(false)
      setEditingRule(null)
    } catch (e) { setError(e.message) }
    finally { setSavingRule(false) }
  }

  async function handleToggleRule(rule) {
    try {
      const updated = await api.updateCalendarRule(rule.id, { enabled: !rule.enabled })
      setRules(prev => prev.map(r => r.id === updated.id ? updated : r))
    } catch (e) { setError(e.message) }
  }

  async function handleDeleteRule(rule) {
    try {
      await api.deleteCalendarRule(rule.id)
      setRules(prev => prev.filter(r => r.id !== rule.id))
      setDeletingRuleId(null)
    } catch (e) { setError(e.message); setDeletingRuleId(null) }
  }

  const sorted = [...activities].sort((a, b) => a.display_order - b.display_order)
  const activityMap = Object.fromEntries(activities.map(a => [a.key, a.label]))

  return (
    <div style={{ minHeight: '100vh', background: '#f0fdf4' }}>
      {/* Header */}
      <div style={{ background: '#14532d', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/" style={{ color: '#86efac', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>← Dashboard</Link>
        <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
        <span style={{ fontSize: 26 }}>🌾</span>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>Gerenciar Atividades</div>
      </div>

      {/* Tabs */}
      <div style={{ background: '#fff', borderBottom: '2px solid #e7f3ea', padding: '0 24px', display: 'flex', gap: 0 }}>
        {[
          { key: 'atividades', label: '📋 Atividades' },
          { key: 'cronograma', label: '📅 Cronograma' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '14px 20px', background: 'transparent', border: 'none', cursor: 'pointer',
            fontWeight: tab === t.key ? 800 : 600, fontSize: 14,
            color: tab === t.key ? '#14532d' : '#64748b',
            borderBottom: tab === t.key ? '3px solid #16a34a' : '3px solid transparent',
            marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{ maxWidth: 680, margin: '32px auto', padding: '0 16px' }}>
        {error && (
          <div style={{ background: '#fee2e2', color: '#7f1d1d', padding: '12px 16px', borderRadius: 10, fontWeight: 600, fontSize: 13, marginBottom: 16 }}>
            Erro: {error}
            <button onClick={() => setError('')} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#7f1d1d', fontWeight: 800 }}>×</button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>Carregando...</div>
        ) : tab === 'atividades' ? (
          <>
            <div style={{ background: '#fef9c3', border: '1.5px solid #fde047', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#713f12', marginBottom: 24 }}>
              ℹ️ Atividades correspondem às colunas de datas da planilha. Você pode renomear, reordenar ou desabilitar cada uma.
              Desabilitar oculta a coluna sem apagar os dados.
            </div>

            <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', overflow: 'hidden', marginBottom: 16 }}>
              {sorted.map((act, idx) => (
                <div key={act.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
                  borderBottom: idx < sorted.length - 1 ? '1px solid #e7f3ea' : 'none',
                  background: act.enabled ? '#fff' : '#f8fafc',
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button onClick={() => handleReorder(idx, 'up')} disabled={idx === 0} style={reorderBtn(idx === 0)}>▲</button>
                    <button onClick={() => handleReorder(idx, 'down')} disabled={idx === sorted.length - 1} style={reorderBtn(idx === sorted.length - 1)}>▼</button>
                  </div>
                  <span style={{ width: 24, height: 24, borderRadius: 999, background: '#f0fdf4', color: '#16a34a', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {idx + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    {editingId === act.id ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveLabel(act); if (e.key === 'Escape') setEditingId(null) }}
                          style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: '1.5px solid #16a34a', fontSize: 14, outline: 'none' }} autoFocus />
                        <button onClick={() => saveLabel(act)} disabled={saving} style={greenBtn}>{saving ? '...' : '✓'}</button>
                        <button onClick={() => setEditingId(null)} style={grayBtn}>✕</button>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontWeight: 700, color: act.enabled ? '#0f172a' : '#94a3b8', fontSize: 14 }}>{act.label}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'DM Mono', monospace", marginTop: 2 }}>{act.key}</div>
                      </div>
                    )}
                  </div>
                  {editingId !== act.id && (
                    <button onClick={() => startEdit(act)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 13, color: '#475569' }} title="Renomear">✏️</button>
                  )}
                  <button onClick={() => handleToggle(act)} style={{
                    padding: '6px 14px', borderRadius: 999, border: 'none', cursor: 'pointer',
                    fontWeight: 700, fontSize: 12,
                    background: act.enabled ? '#dcfce7' : '#f1f5f9',
                    color: act.enabled ? '#15803d' : '#94a3b8',
                  }}>{act.enabled ? '● Ativa' : '○ Oculta'}</button>
                  {editingId !== act.id && (
                    deletingId === act.id ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: '#7f1d1d', fontWeight: 600 }}>Confirmar?</span>
                        <button onClick={() => handleDelete(act)} style={redBtn}>Sim</button>
                        <button onClick={() => setDeletingId(null)} style={grayBtn}>Não</button>
                      </div>
                    ) : (
                      <button onClick={() => setDeletingId(act.id)} style={{ background: 'transparent', border: '1px solid #fca5a5', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', fontSize: 13, color: '#dc2626' }}>🗑️</button>
                    )
                  )}
                </div>
              ))}
            </div>

            {showNewForm ? (
              <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', padding: '16px 20px' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 12 }}>Nova atividade</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={newLabel} onChange={e => setNewLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setShowNewForm(false); setNewLabel('') } }}
                    placeholder="Nome da atividade (ex: Adubação de Cobertura)"
                    style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid #16a34a', fontSize: 14, outline: 'none' }} autoFocus />
                  <button onClick={handleCreate} disabled={creating || !newLabel.trim()} style={{ ...greenBtn, opacity: creating || !newLabel.trim() ? 0.6 : 1 }}>{creating ? '...' : 'Criar'}</button>
                  <button onClick={() => { setShowNewForm(false); setNewLabel('') }} style={grayBtn}>Cancelar</button>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>A key interna será gerada automaticamente a partir do nome.</div>
              </div>
            ) : (
              <button onClick={() => setShowNewForm(true)} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '2px dashed #86efac', background: 'transparent', cursor: 'pointer', color: '#16a34a', fontWeight: 700, fontSize: 14 }}>
                + Nova atividade
              </button>
            )}
          </>
        ) : (
          /* ── Aba Cronograma ─────────────────────────────────────────────── */
          <>
            <div style={{ background: '#e0f2fe', border: '1.5px solid #7dd3fc', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#075985', marginBottom: 24 }}>
              📅 Configure sequências automáticas: ao marcar uma atividade como realizada, o sistema agendará
              as próximas automaticamente com base nos intervalos definidos aqui.
            </div>

            <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', overflow: 'hidden', marginBottom: 16 }}>
              {rules.length === 0 ? (
                <div style={{ padding: '32px 24px', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                  Nenhuma regra configurada ainda.
                </div>
              ) : (
                rules.map((rule, idx) => (
                  <div key={rule.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
                    borderBottom: idx < rules.length - 1 ? '1px solid #e7f3ea' : 'none',
                    background: rule.enabled ? '#fff' : '#f8fafc',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ background: '#dcfce7', color: '#14532d', borderRadius: 6, padding: '3px 10px', fontSize: 13, fontWeight: 700 }}>
                          {activityMap[rule.trigger_key] ?? rule.trigger_key}
                        </span>
                        <span style={{ color: '#64748b', fontSize: 13 }}>→</span>
                        <span style={{ background: '#dbeafe', color: '#1e3a5f', borderRadius: 6, padding: '3px 10px', fontSize: 13, fontWeight: 700 }}>
                          {activityMap[rule.target_key] ?? rule.target_key}
                        </span>
                        <span style={{ color: '#64748b', fontSize: 13 }}>
                          em <strong>{rule.offset_days}</strong> {rule.offset_days === 1 ? 'dia' : 'dias'}
                        </span>
                      </div>
                    </div>

                    <button onClick={() => handleToggleRule(rule)} style={{
                      padding: '5px 12px', borderRadius: 999, border: 'none', cursor: 'pointer',
                      fontWeight: 700, fontSize: 11,
                      background: rule.enabled ? '#dcfce7' : '#f1f5f9',
                      color: rule.enabled ? '#15803d' : '#94a3b8',
                    }}>{rule.enabled ? '● Ativa' : '○ Pausada'}</button>

                    <button onClick={() => openEditRule(rule)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 13, color: '#475569' }}>✏️</button>

                    {deletingRuleId === rule.id ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: '#7f1d1d', fontWeight: 600 }}>Confirmar?</span>
                        <button onClick={() => handleDeleteRule(rule)} style={redBtn}>Sim</button>
                        <button onClick={() => setDeletingRuleId(null)} style={grayBtn}>Não</button>
                      </div>
                    ) : (
                      <button onClick={() => setDeletingRuleId(rule.id)} style={{ background: 'transparent', border: '1px solid #fca5a5', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', fontSize: 13, color: '#dc2626' }}>🗑️</button>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Formulário de criação/edição de regra */}
            {showRuleForm ? (
              <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', padding: '20px 24px', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 16 }}>
                  {editingRule ? 'Editar regra' : 'Nova regra de cronograma'}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>Ao concluir</label>
                    <select value={ruleForm.trigger_key} onChange={e => setRuleForm(f => ({ ...f, trigger_key: e.target.value }))} style={selectStyle}>
                      <option value="">Selecionar atividade...</option>
                      {sorted.map(a => (
                        <option key={a.key} value={a.key}>{a.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ color: '#64748b', fontWeight: 700, fontSize: 18, textAlign: 'center', paddingTop: 20 }}>→</div>
                  <div>
                    <label style={labelStyle}>Agendar</label>
                    <select value={ruleForm.target_key} onChange={e => setRuleForm(f => ({ ...f, target_key: e.target.value }))} style={selectStyle}>
                      <option value="">Selecionar atividade...</option>
                      {sorted.filter(a => a.key !== ruleForm.trigger_key).map(a => (
                        <option key={a.key} value={a.key}>{a.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Intervalo (dias após a data de realização)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="number"
                      min={1}
                      max={999}
                      value={ruleForm.offset_days}
                      onChange={e => setRuleForm(f => ({ ...f, offset_days: parseInt(e.target.value) || 1 }))}
                      style={{ width: 80, padding: '8px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 14, outline: 'none' }}
                    />
                    <span style={{ fontSize: 13, color: '#64748b' }}>dias</span>
                    {ruleForm.trigger_key && ruleForm.target_key && ruleForm.offset_days > 0 && (
                      <span style={{ fontSize: 12, color: '#4ade80', background: '#052e16', borderRadius: 6, padding: '4px 10px' }}>
                        Ex: plantio em 01/10 → {activityMap[ruleForm.target_key] || '...'} em {formatExampleDate(ruleForm.offset_days)}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleSaveRule}
                    disabled={savingRule || !ruleForm.trigger_key || !ruleForm.target_key || !ruleForm.offset_days}
                    style={{ ...greenBtn, opacity: savingRule || !ruleForm.trigger_key || !ruleForm.target_key ? 0.6 : 1, padding: '10px 20px' }}>
                    {savingRule ? 'Salvando...' : 'Salvar regra'}
                  </button>
                  <button onClick={() => { setShowRuleForm(false); setEditingRule(null) }} style={{ ...grayBtn, padding: '10px 16px' }}>Cancelar</button>
                </div>
              </div>
            ) : (
              <button onClick={openNewRule} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '2px dashed #7dd3fc', background: 'transparent', cursor: 'pointer', color: '#0369a1', fontWeight: 700, fontSize: 14 }}>
                + Nova regra de cronograma
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function formatExampleDate(offsetDays) {
  const d = new Date(2026, 9, 1)  // 01/10
  d.setDate(d.getDate() + offsetDays)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

function reorderBtn(disabled) {
  return {
    background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 4,
    cursor: disabled ? 'default' : 'pointer', fontSize: 10,
    color: disabled ? '#cbd5e1' : '#64748b', padding: '1px 5px', lineHeight: 1, display: 'block',
  }
}

const greenBtn = {
  background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8,
  padding: '6px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 13,
}
const grayBtn = {
  background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8,
  padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
}
const redBtn = {
  background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8,
  padding: '5px 10px', cursor: 'pointer', fontWeight: 700, fontSize: 12,
}
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
}
const selectStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  border: '1.5px solid #d1d5db', fontSize: 14, outline: 'none',
  background: '#fff', cursor: 'pointer',
}
